/**
 * Player Component
 */

import * as actions from '../../../../redux/actions/player';
import * as uiActions from '../../../../redux/actions/ui';
import {
  getCurrentTrack,
  getIsTrackPlaying,
  getTermsForCurrentlyPlayingTrack,
  isTermListOpen,
  isTrackDetailsOpen,
} from '../../../../redux/selectors/player';
import { isAddToProjectOpen, isCurrentPlayingTrackInProject } from '../../../../redux/selectors/projects';
import { transparent } from '../../../styles/colors';
import { baseline } from '../../../styles/grid';
import './styles.scss';

import * as React from 'react';
import AudioPlayer from 'react-audio-player';
import * as BEM from 'react-bem-helper';
import { connect } from 'react-redux';
import { VelocityComponent } from 'velocity-react';

import Button, { ButtonProps } from '../../../atoms/buttons/Button';
import {
  IconFilter,
  IconMore,
  IconPlayerNext,
  IconPlayerPause,
  IconPlayerPlay,
  IconPlayerPrevious,
  IconProject,
  IconProjectSelected,
} from '../../../atoms/images/Icon';
import Thumbnail from '../../../atoms/images/Thumbnail';
import Heading from '../../../atoms/text/Heading';
import { Span } from '../../../atoms/text/Label';
import { TrackPagePath } from '../../../pages/Track';
import PlayerProgressBar from './PlayerProgressBar';
import PlayerTrackTerms from './PlayerTrackTerms';

interface IOwnStateProps extends Pick<IPlayerState, 'currentTrack' | 'isTrackPlaying'> {
  addToProjectIsOpen: boolean;
  termListIsOpen: boolean;
  trackDetailsIsOpen: boolean;
  trackIsInProjects: boolean;
  trackTerms: GQL.ITaxonomyTerm[] | undefined;
}

interface IOwnDispatchProps {
  pause(): void;
  play(): void;
  playNext(): void;
  playPrevious(): void;
  togglePlay(): void;
  toggleAddToProjectVisibility(visible: boolean): void;
  toggleTermListVisibility(visible: boolean): void;
  toggleTrackDetailsVisibility(visible: boolean): void;
  updateCurrentTrackElapsedTime(elapsedTime: number): void;
}

interface IOwnMergedProps {
  addToProject(): void;
  showTrackDetails(): void;
}

interface IOwnState {
  duration: number;
  elapsedTime: number;
  metadataLoaded: boolean;
  remainingTime: number;
  isInProject: boolean;
  termListIsOpen: boolean;
  isTrackPlaying: boolean;
}

type PlayerProps = IOwnDispatchProps & IOwnStateProps & IOwnMergedProps;

const classes = new BEM('player');

class Player extends React.Component<PlayerProps, IOwnState> {
  private audioPlayer: HTMLAudioElement;
  private showTermListTimer: number;

  constructor(props) {
    super(props);

    this.state = {
      duration: 0,
      elapsedTime: 0,
      isInProject: false,
      termListIsOpen: false,
      isTrackPlaying: true,
      metadataLoaded: false,
      remainingTime: 0,
    };
  }

  componentDidMount() {
    if (this.audioPlayer) {
      this.audioPlayer.volume = 1;
    }

    document.addEventListener('keydown', this.onKeyDown);

    this.showTermListTimer = window.setTimeout(() => {
      this.setState({ termListIsOpen: true }, () => {
        setTimeout(() => {
          this.props.toggleTermListVisibility(this.state.termListIsOpen);
        }, 400);
      });
    }, 2000);
  }

  componentWillUnmount() {
    clearTimeout(this.showTermListTimer);
    document.removeEventListener('keydown', this.onKeyDown);
  }

  componentDidUpdate(prevProps: PlayerProps, prevState: IOwnState) {
    if (this.props.isTrackPlaying && !prevProps.isTrackPlaying) {
      this.audioPlayer.play();
    } else if (!this.props.isTrackPlaying && prevProps.isTrackPlaying) {
      this.audioPlayer.pause();
    }

    if (this.props.currentTrack && prevProps.currentTrack) {
      if (this.props.currentTrack.audioFile !== prevProps.currentTrack.audioFile) {
        this.setState({
          duration: this.props.currentTrack.duration,
          elapsedTime: 0,
          metadataLoaded: false,
          remainingTime: this.props.currentTrack.duration,
        });
      } else {
        if (this.props.currentTrack.elapsedTime !== prevProps.currentTrack.elapsedTime) {
          this.setState({
            elapsedTime: this.props.currentTrack.elapsedTime,
            // tslint:disable-next-line:max-line-length
            remainingTime: this.state.remainingTime - (this.props.currentTrack.elapsedTime - prevProps.currentTrack.elapsedTime),
          });
        }
      }
    }

    if (prevProps.isTrackPlaying !== this.props.isTrackPlaying) {
      setTimeout(() => {
        if (this.state.isTrackPlaying !== this.props.isTrackPlaying) {
          this.setState({ isTrackPlaying: this.props.isTrackPlaying });
        }
      }, 100);
    }

    if (prevProps.termListIsOpen !== this.props.termListIsOpen
    && this.props.termListIsOpen !== this.state.termListIsOpen) {
      this.onClickTermsButton();
    }
  }

  onKeyDown = ({ key, srcElement, target }: KeyboardEvent) => {
    const currentTarget = target as HTMLElement || srcElement;
    const targetTagName = currentTarget.tagName.toLowerCase();

    if (['input', 'textarea'].indexOf(targetTagName) > -1) {
      return;
    }

    switch (key) {
      case ' ':
        this.props.togglePlay();
        break;
      case 'ArrowRight':
        this.props.playNext();
        break;
      case 'ArrowLeft':
        this.props.playPrevious();
        break;
      case 'Enter':
        this.audioPlayer.currentTime += this.state.duration * .2;
        break;
      case 'Backspace':
        this.audioPlayer.currentTime -= this.state.duration * .2;
        break;
      case '1':
      case '2':
      case '3':
      case '4':
      case '5':
      case '6':
      case '7':
      case '8':
      case '9':
      case '0':
        this.audioPlayer.currentTime = this.state.duration * (parseInt(key, 10) / 10);
        break;
      case 'i':
        this.onClickDetailsButton();
        break;
    }
  }

  onListenTick = (e) => {
    if (this.audioPlayer) {
      this.props.updateCurrentTrackElapsedTime(this.audioPlayer.currentTime);
    }
  }

  onLoadedMetadata = (e) => {
    const { duration } = this.audioPlayer;

    this.setState({
      duration,
      metadataLoaded: true,
      remainingTime: duration,
    });
  }

  willSeek = (percentage: number) => {
    this.audioPlayer.currentTime = this.state.duration * percentage;
  }

  onSeeked = () => {
    this.props.play();
  }

  onTrackEnded = () => {
    this.props.playNext();
  }

  onClickDetailsButton = () => {
    if (this.props.trackDetailsIsOpen) {
      this.props.toggleTrackDetailsVisibility(false);
    } else {
      this.props.showTrackDetails();
    }
  }

  onClickProjectButton = () => {
    if (this.props.addToProjectIsOpen) {
      this.props.toggleAddToProjectVisibility(false);
    } else {
      this.props.addToProject();
    }
  }

  onClickTermsButton = () => {
    this.setState((prevState: IOwnState) => ({ termListIsOpen: !prevState.termListIsOpen }), () => {
      setTimeout(() => {
        this.props.toggleTermListVisibility(this.state.termListIsOpen);
      }, this.state.termListIsOpen ? 400 : 0);
    });
  }

  render() {
    const {
      addToProjectIsOpen,
      trackDetailsIsOpen,
      trackIsInProjects,
      trackTerms,
    } = this.props;

    const {
      duration,
      elapsedTime,
      termListIsOpen,
      isTrackPlaying,
      metadataLoaded,
      remainingTime,
    } = this.state;

    const currentTrack = this.props.currentTrack!;

    const imageInnerProps: any = {};

    if (currentTrack.image) {
      imageInnerProps.style = { backgroundImage: `url(${currentTrack.imageBlurred})` };
    }

    const ProjectIcon = trackIsInProjects ? IconProjectSelected : IconProject;
    const optionButtonProps: Partial<ButtonProps> = {
      variant: 'icon',
      className: 'track__option',
    };

    const classModifiers = {
      'is-playing': isTrackPlaying,
      'is-buffering': !metadataLoaded,
    };

    return (
      <div {...classes('', classModifiers) }>
        <AudioPlayer
          ref={(node) => {
            if (node && node.audioEl) {
              this.audioPlayer = node.audioEl;
            }
          }}
          src={`/${currentTrack.audioFile}`}
          listenInterval={250}
          onEnded={this.onTrackEnded}
          onListen={this.onListenTick}
          onLoadedMetadata={this.onLoadedMetadata}
          onSeeked={this.onSeeked}
          autoPlay
        />
        <VelocityComponent
          animation={{ translateY: termListIsOpen ? [0, '100%'] : ['100%', 0] }}
          duration={250}
          display={termListIsOpen ? 'block' : 'none'}
        >
          <PlayerTrackTerms
            track={currentTrack.trackInfo}
            onClose={this.onClickTermsButton}
            open={termListIsOpen}
            slug={currentTrack.trackInfo.slug!}
            terms={trackTerms!}
          />
        </VelocityComponent>
        <div {...classes('background') }>
          <div {...classes('background__inner') } {...imageInnerProps} />
        </div>
        <div {...classes('wrapper') }>
          <div {...classes('controls') }>
            <Button
              onClick={this.props.playPrevious}
              icon={<IconPlayerPrevious />}
              variant="icon"
              disabled={currentTrack.isFirstInQueue}
              backgroundColor={transparent}
            />
            <Button
              onClick={isTrackPlaying ? this.props.pause : this.props.play}
              icon={isTrackPlaying ? <IconPlayerPause /> : <IconPlayerPlay />}
              variant="icon"
              backgroundColor={transparent}
            />
            <Button
              onClick={this.props.playNext}
              icon={<IconPlayerNext />}
              variant="icon"
              disabled={currentTrack.isLastInQueue}
              backgroundColor={transparent}
            />
          </div>
          <div {...classes('track-details') }>
            <Thumbnail src={currentTrack.image} size={baseline * 7} {...classes('track-image') } />
            <div {...classes('track-info') }>
              <Heading
                is="h5"
                text={currentTrack.title}
                maxTextLength={15}
                hyperLinkProps={{
                  href: TrackPagePath(currentTrack.trackInfo.slug!),
                }}
                {...classes('track-info__title') }
              />
              <Span
                text={currentTrack.artist}
                size="small"
                textColor="bw7"
                {...classes('track-info__subtitle') }
              />
            </div>
            <Button
              onClick={this.onClickProjectButton}
              icon={<ProjectIcon />}
              {...optionButtonProps}
              className={addToProjectIsOpen ? 'track__option track__option--active' : 'track__option'}
            />
            <Button
              onClick={this.onClickTermsButton}
              icon={<IconFilter />}
              {...optionButtonProps}
              className={termListIsOpen ?
                'track__option track__option--filters track__option--active'
                : 'track__option track__option--filters'}
            />
            <Button
              onClick={this.onClickDetailsButton}
              icon={<IconMore />}
              {...optionButtonProps}
              className={trackDetailsIsOpen ? 'track__option track__option--active' : 'track__option'}
            />
          </div>
          <PlayerProgressBar
            duration={duration}
            elapsedTime={elapsedTime}
            metadataLoaded={metadataLoaded}
            onSeek={this.willSeek}
            onDragStart={this.props.pause}
            remainingTime={remainingTime ? -remainingTime : currentTrack.duration}
          />
        </div>
      </div>
    );
  }
}

const ConnectedPlayer = connect<IOwnStateProps, IOwnDispatchProps & IDispatchProps, {}, PlayerProps, IStoreState>(
  (state) => ({
    addToProjectIsOpen: isAddToProjectOpen(state),
    currentTrack: getCurrentTrack(state),
    isTrackPlaying: getIsTrackPlaying(state),
    termListIsOpen: isTermListOpen(state),
    trackDetailsIsOpen: isTrackDetailsOpen(state),
    trackIsInProjects: isCurrentPlayingTrackInProject(state),
    trackTerms: getTermsForCurrentlyPlayingTrack(state),
  }),
  (dispatch) => ({
    dispatch,
    pause: () => dispatch(actions.pause()),
    play: () => dispatch(actions.play()),
    playNext: () => dispatch(actions.playNext()),
    playPrevious: () => dispatch(actions.playPrevious()),
    togglePlay: () => dispatch(actions.togglePlay()),
    toggleAddToProjectVisibility: (visible) => dispatch(uiActions.toggleAddToProjectVisibility(visible)),
    toggleTermListVisibility: (visible) => dispatch(uiActions.togglePlayerTermListVisibility(visible)),
    toggleTrackDetailsVisibility: (visible) => dispatch(uiActions.toggleTrackDetailsVisibility(visible)),
    updateCurrentTrackElapsedTime: (elapsedTime) => dispatch(actions.updateCurrentTrackElapsedTime({ elapsedTime })),
  }),
  (stateProps, { dispatch, ...dispatchProps }) => ({
    ...stateProps,
    ...dispatchProps,
    addToProject: () => {
      const {trackId, version } = stateProps.currentTrack!;
      return dispatch(uiActions.showAddTrackToProjectForm({ trackId, trackVersion: version }));
    },
    showTrackDetails: () => dispatch(uiActions.showTrackDetails({ trackInfo: stateProps.currentTrack!.trackInfo })),
  }),
)(Player);

export default ConnectedPlayer;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/public/components/organisms/global/Player/index.tsx